import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  NativeSelect,
  Paper,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import api from '../../../services/api';
import { toast } from 'react-toastify';

const useModalStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 8,
    border: '2px solid #ddd',
    overflow: 'hidden',
    maxWidth: 520,
  },
  title: {
    backgroundColor: '#C4C4C4',
    margin: 0,
    padding: '14px 24px',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  titleText: {
    margin: 0,
    padding: 0,
    textAlign: 'center',
    color: '#000',
    fontWeight: 'bold',
    fontSize: 22,
    lineHeight: 1.3,
    pointerEvents: 'none',
    width: '100%',
    maxWidth: '100%',
  },
  content: {
    backgroundColor: '#eee',
    padding: '20px 24px',
    borderTop: '1px solid #ddd',
  },
  fieldBlock: {
    marginBottom: 16,
    '&:last-child': {
      marginBottom: 0,
    },
  },
  label: {
    color: 'rgba(0, 0, 0, 0.54)',
    fontSize: 13,
    fontWeight: 'bold',
    marginBottom: 7,
    display: 'block',
    width: '100%',
  },
  selectPaper: {
    backgroundColor: '#FFF',
    borderRadius: 5,
    padding: '0',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    border: '1px solid rgba(0, 0, 0, 0.23)',
    boxSizing: 'border-box',
  },
  nativeSelect: {
    width: '100%',
    padding: '10px 12px',
    fontSize: 16,
    color: '#949494',
    textTransform: 'uppercase',
    '&:focus': {
      backgroundColor: 'transparent',
    },
  },
  textFieldRoot: {
    width: '100%',
    backgroundColor: '#fff',
    borderRadius: 5,
  },
  textFieldInput: {
    fontSize: 16,
    color: '#949494',
    padding: '12px 14px',
  },
  actions: {
    padding: '16px 24px',
    backgroundColor: '#eee',
    borderTop: '1px solid #ddd',
    gap: 12,
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  /** Mesmo tamanho do “Enviar”; cor da barra; hover com elevação. */
  btnCancel: {
    backgroundColor: '#C4C4C4',
    color: '#000',
    height: 40,
    minWidth: 180,
    width: 180,
    boxSizing: 'border-box',
    borderRadius: 4,
    textTransform: 'capitalize',
    fontSize: 14,
    lineHeight: 1.5,
    padding: '8px 12px',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#b8b8b8',
      boxShadow: theme.shadows[4],
    },
    '&.Mui-disabled': {
      backgroundColor: '#d4d4d4',
      color: 'rgba(0, 0, 0, 0.38)',
    },
  },
  /** Mesmo tamanho do “Cancelar”; cor primary como “Gerar Relatório”. */
  btnEnviar: {
    height: 40,
    minWidth: 180,
    width: 180,
    boxSizing: 'border-box',
    lineHeight: 1.5,
    textTransform: 'capitalize',
    fontSize: 14,
    padding: '8px 12px',
  },
}));

export function ControleEmailTesteModal({ open, onClose, relatorioDinamicoId }) {
  const classes = useModalStyles();
  const [ancoras, setAncoras] = useState([]);
  const [ancoraId, setAncoraId] = useState('');
  const [emailDestinatario, setEmailDestinatario] = useState('');
  const [loadingLista, setLoadingLista] = useState(false);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      setLoadingLista(true);
      try {
        const res = await api.get('Ancoras/Consultar?status=Ativo');
        if (cancel) return;
        if (res?.data?.status === 'Sucesso' && Array.isArray(res.data.resposta)) {
          setAncoras(res.data.resposta);
        }
      } catch {
        toast.error('Não foi possível carregar as redes.');
      } finally {
        if (!cancel) setLoadingLista(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setAncoraId('');
      setEmailDestinatario('');
      setEnviando(false);
    }
  }, [open]);

  const enviar = async () => {
    const aid = Number(ancoraId);
    if (!aid || !emailDestinatario.trim()) {
      toast.warning('Selecione a rede e informe o e-mail do destinatário.');
      return;
    }
    if (!relatorioDinamicoId) {
      toast.error('Relatório inválido.');
      return;
    }
    setEnviando(true);
    try {
      const res = await api.post('RelatoriosDinamicos/EnviarEmailTesteRede', {
        relatorioDinamicoId,
        ancoraId: aid,
        emailDestinatario: emailDestinatario.trim(),
      });
      if (res?.data?.status === 'Sucesso') {
        toast.success('E-mail enfileirado. O envio será feito pelo serviço em background.');
        onClose();
      } else if (res?.data?.resposta?.length) {
        res.data.resposta.forEach((el) => toast.error(el.message || 'Erro ao enfileirar.'));
      }
    } catch (e) {
      toast.error(e?.message || 'Falha na requisição.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={enviando ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      <DialogTitle className={classes.title} disableTypography>
        <span className={classes.titleText}>Teste de Envio de E-Mail</span>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <div className={classes.fieldBlock}>
          <span className={classes.label}>Rede</span>
          <Paper className={classes.selectPaper} elevation={0}>
            <NativeSelect
              fullWidth
              disableUnderline
              className={classes.nativeSelect}
              value={ancoraId}
              onChange={(e) => setAncoraId(e.target.value)}
              disabled={loadingLista || enviando}
              inputProps={{ 'aria-label': 'Rede' }}
            >
              <option value="">Selecione a rede</option>
              {ancoras.map((a) => (
                <option key={a.ancoraId} value={a.ancoraId}>
                  {a.razaoSocial || a.apelido || `Rede ${a.ancoraId}`}
                </option>
              ))}
            </NativeSelect>
          </Paper>
        </div>
        <div className={classes.fieldBlock}>
          <span className={classes.label}>E-mail do destinatário</span>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            type="email"
            value={emailDestinatario}
            onChange={(e) => setEmailDestinatario(e.target.value)}
            disabled={enviando}
            placeholder="nome@dominio.com"
            className={classes.textFieldRoot}
            InputProps={{
              classes: { input: classes.textFieldInput },
            }}
            inputProps={{ autoComplete: 'email' }}
          />
        </div>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={onClose}
          disabled={enviando}
          variant="contained"
          className={classes.btnCancel}
        >
          Cancelar
        </Button>
        <Button
          onClick={enviar}
          disabled={enviando || loadingLista}
          variant="contained"
          color="primary"
          className={classes.btnEnviar}
        >
          Enviar E-mail
        </Button>
      </DialogActions>
    </Dialog>
  );
}
